import Carousel from '@/components/Carousel';
import { StyledEmbla } from '@/components/Carousel/styled';
import { NarrativeCardPopulated } from '@/components/NarrativeCard';
import { useTracking } from 'react-tracking';
import { Button, cn } from '@simplywallst/ui-core/next';
import { useNarrativesRelated } from '../hooks/useNarrativesRelated';
import { NarrativesEmptyPanel } from '../../NarrativesEmptyPanel';
import { VISIBLE_NARRATIVES } from '../utils';
import { NarrativeInvalidationBoundary } from '../../NarrativeInvalidationBoundary';

type Props = {
  slug: string;
};

export const NarrativesRelatedList = ({ slug }: Props) => {
  const { trackEvent } = useTracking();
  // We overfetch by 1 narrative to determine if there are more than VISIBLE_NARRATIVES
  const { data: NarrativeResponse } = useNarrativesRelated({
    slug,
    count: VISIBLE_NARRATIVES + 1,
  });
  const { company, related: narratives } = NarrativeResponse.narrative;

  const handleOnClick = (narrative: (typeof narratives)[number]) => {
    const { id, company } = narrative;
    return trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: id,
      companyId: company?.id,
      uniqueSymbol: company?.tickerSymbol,
    });
  };

  if (narratives.length === 0)
    return (
      <NarrativesEmptyPanel>
        <div className="grid gap-x1">
          <div>There are no other narratives for this company.</div>
          <Button
            variant="primary"
            className="min-w-56"
            href="/community/narratives"
          >
            View all narratives
          </Button>
        </div>
      </NarrativesEmptyPanel>
    );

  const hasMoreThanOneNarrative = narratives.length > 1;

  return (
    <div className="-mt-x2 grid">
      <NarrativeInvalidationBoundary
        onSetPrimary={({ invalidatePriority }) =>
          invalidatePriority(['narrative-related-list'])
        }
      >
        <StyledEmbla className="mt-x2">
          <Carousel
            align="center"
            containScroll="trimSnaps"
            showArrows={hasMoreThanOneNarrative}
            showGradient={hasMoreThanOneNarrative}
            totalSlides={VISIBLE_NARRATIVES}
          >
            <div
              role="list"
              className={cn(
                'grid w-full gap-x1',
                hasMoreThanOneNarrative
                  ? 'grid-cols-[repeat(4,317px)]'
                  : 'grid-cols-[minmax(317px,500px)]'
              )}
            >
              {narratives.slice(0, VISIBLE_NARRATIVES).map((narrative) => (
                <div role="listitem" key={narrative.id}>
                  <NarrativeCardPopulated
                    showCompanyIcon
                    narrative={narrative}
                    onClick={() => handleOnClick(narrative)}
                    titleAs="h3"
                  />
                </div>
              ))}
            </div>
          </Carousel>
        </StyledEmbla>
      </NarrativeInvalidationBoundary>

      {company?.canonicalCommunityUrl &&
        narratives.length > VISIBLE_NARRATIVES && (
          <div className="mt-x2 grid grid-cols-1">
            <Button
              variant="secondary"
              href={company?.canonicalCommunityUrl}
              className="w-full"
            >
              View all narratives
            </Button>
          </div>
        )}
    </div>
  );
};
